/**
 * Converts flat entity data to the nested JSON structure format for visualization
 */

/**
 * Helper function to transform entity data from SingPass format to the application's expected format
 * @param entityData - The raw entity data from SingPass
 * @returns Formatted entity data for use with setEntityData
 */

export const formatEntityDataForRedux = (entityData: any) => {
  if (!entityData) return entityData

  // Create the nested JSON structure from flat data
  if (entityData.entityName || entityData.uen || entityData.businessExpiryDate) {
    // Parse SSIC data to separate code and description
    const parsePrimarySSIC = () => {
      if (!entityData.primarySSIC) return { code: '', desc: '' }
      const match = entityData.primarySSIC.match(/^([\d]+)\s*-\s*(.*)$/)
      if (match) {
        return { code: match[1], desc: match[2] }
      }
      return { code: entityData.primarySSIC, desc: '' }
    }

    const parseSecondarySSIC = () => {
      if (!entityData.secondarySSIC) return { code: '', desc: '' }
      const match = entityData.secondarySSIC.match(/^([\d]+)\s*-\s*(.*)$/)
      if (match) {
        return { code: match[1], desc: match[2] }
      }
      return { code: entityData.secondarySSIC, desc: '' }
    }

    // Parse address function

    // Call the nested functions we defined above
    const primarySSIC = parsePrimarySSIC()
    const secondarySSIC = parseSecondarySSIC()
    // Create the nested structure
    const nestedData = {
      entity: {
        'basic-profile': {
          'entity-name': {
            value: entityData.entityName || '',
          },
          'entity-type': {
            desc: entityData.entityType || '',
          },
          'entity-status': {
            value: entityData.entityStatus || '',
          },
          'business-expiry-date': {
            value: entityData.businessExpiryDate || '',
          },
          'registration-date': {
            value: entityData.registrationDate || '',
          },
          'country-of-incorporation': {
            desc: entityData.countryOfIncorporation || '',
          },
          'business-constitution': {
            desc: entityData.businessConstitution || '',
          },
          'primary-activity': {
            code: primarySSIC.code,
            desc: primarySSIC.desc,
          },
          'secondary-activity': {
            code: secondarySSIC.code,
            desc: secondarySSIC.desc,
          },
          ownership: {
            desc: entityData.ownership || '',
          },
          'company-type': {
            desc: entityData.companyType || '',
          },
          uen: {
            value: entityData.uen || '',
          },
          address: entityData.enregadd ? { line1: { value: entityData.enregadd } } : {},
        },
        addresses: {
          'addresses-list': entityData.enregadd ? [{ line1: { value: entityData.enregadd } }] : [],
        },
        // Financials highlights (array)
        financials: (() => {
          const highlightsSrc = Array.isArray(entityData.financials) ? entityData.financials : []
          return {
            highlights: highlightsSrc.map((f: any) => ({
              currency: f.currency || {},
              'is-audited': f['is-audited'] || { value: f.isAudited },
              'company-revenue': f['company-revenue'] || { value: f.companyRevenue },
              'current-period-end-date': f['current-period-end-date'] || {
                value: f.currentPeriodEndDate,
              },
              'current-period-start-date': f['current-period-start-date'] || {
                value: f.currentPeriodStartDate,
              },
              'company-profit-loss-after-tax': f['company-profit-loss-after-tax'] || {
                value: f.companyProfitLossAfterTax,
              },
              'company-profit-loss-before-tax': f['company-profit-loss-before-tax'] || {
                value: f.companyProfitLossBeforeTax,
              },
            })),
          }
        })(),
        // Capitals list (array)
        capitals: (() => {
          const capitalsSrc = Array.isArray(entityData.capitals) ? entityData.capitals : []
          return {
            'capitals-list': capitalsSrc.map((c: any) => ({
              currency: c.currency || {},
              'capital-type': c['capital-type'] || c.capitalType || {},
              'issued-capital-amount': c['issued-capital-amount'] || {
                value: c.issuedCapitalAmount,
              },
              'share-allotted-amount': c['share-allotted-amount'] || {
                value: c.shareAllottedAmount,
              },
              'paid-up-capital-amount': c['paid-up-capital-amount'] || {
                value: c.paidUpCapitalAmount,
              },
            })),
          }
        })(),
        // Shareholders list (array)
        shareholders: (() => {
          const shSrc = Array.isArray(entityData.shareholdersList)
            ? entityData.shareholdersList
            : entityData.shareholders?.['shareholders-list'] || []
          return {
            'shareholders-list': shSrc.map((s: any) => ({
              category: s.category || {},
              currency: s.currency || {},
              allocation: s.allocation || { value: s.allocationValue || s.allocationAmount },
              'share-type': s['share-type'] || s.shareType || {},
              // Support either person-reference or entity-reference, pass-through if present
              'person-reference': s['person-reference'] || {},
              'entity-reference': s['entity-reference'] || {},
            })),
          }
        })(),
        // Appointments list (array)
        appointments: (() => {
          const apptSrc = Array.isArray(entityData.appointments)
            ? entityData.appointments
            : entityData.appointments?.['appointments-list'] || []
          return {
            'appointments-list': apptSrc.map((a: any) => ({
              category: a.category || {},
              position: a.position || {},
              'corppass-email': a['corppass-email'] || a.corppassEmail,
              'appointment-date': a['appointment-date'] || { value: a.appointmentDate },
              'person-reference': a['person-reference'],
              'entity-reference': a['entity-reference'],
            })),
          }
        })(),
      },

      person: {
        uinfin: entityData.uinfin ? { value: entityData.uinfin } : undefined,
        name: entityData.name ? { value: entityData.name } : undefined,
        sex: entityData.sex ? { desc: entityData.sex } : undefined,
        nationality: entityData.nationality ? { desc: entityData.nationality } : undefined,
        dob: entityData.dob ? { value: entityData.dob } : undefined,
        email: entityData.email ? { value: entityData.email } : undefined,
        mobileno: (() => {
          if (!entityData.mobileno) return undefined
          const mobile: string = String(entityData.mobileno).replace(/\s+/g, '')
          // Basic parse: extract prefix like +65 and the remaining digits
          const prefixMatch = mobile.match(/^(\+\d{1,3})/)
          const prefix = prefixMatch ? prefixMatch[1] : ''
          const rest = mobile.replace(/^(\+\d{1,3})/, '')
          // Derive areacode (first 3) and nbr (remaining) if possible
          const areacode = rest.length >= 3 ? rest.slice(0, 3) : ''
          const nbr = rest.length > 3 ? rest.slice(3) : rest
          return {
            prefix: prefix ? { value: prefix } : undefined,
            areacode: areacode ? { value: areacode } : undefined,
            nbr: nbr ? { value: nbr } : undefined,
          }
        })(),
        // Fallback: if we only have a single-line address string, put it under line1
        regadd: entityData.regadd
          ? (() => {
              const addr = String(entityData.regadd).trim()
              // Detect SG by common postal marker like "S(######)" or explicit mention of Singapore
              const isSG = /\bS\(\d{6}\)/i.test(addr) || /\bSingapore\b/i.test(addr)
              // Try to parse SG address of form: [BLK ]<block> <building> <street> S(<postal>)
              // Example: "BLK 171A 18-416 EDGEDALE PLAINS S(821171)"
              const sgMatch = addr.match(
                /^\s*(?:BLK\s+)?([A-Z0-9]+)\s+([A-Z0-9-]+)\s+(.+?)\s+S\((\d{6})\)\s*$/i
              )
              if (sgMatch) {
                const block = sgMatch[1]
                const building = sgMatch[2]
                const street = sgMatch[3]
                const postal = sgMatch[4]
                return {
                  type: 'SG',
                  block: { value: block.trim() },
                  building: { value: building.trim() },
                  street: { value: street.trim() },
                  postal: { value: postal.trim() },
                }
              }
              // Fallback: keep previous behavior
              return {
                ...(isSG ? { type: 'SG' } : {}),
                line1: { value: addr },
              }
            })()
          : undefined,
        residentialstatus: entityData.residentialstatus
          ? { desc: entityData.residentialstatus }
          : undefined,
        ownerprivate:
          typeof entityData.ownerprivate !== 'undefined'
            ? { value: Boolean(entityData.ownerprivate) }
            : undefined,
        noahistory: (() => {
          const src = Array.isArray(entityData.noahistory) ? entityData.noahistory : []
          return src.map((n: any) => ({
            rent:
              n?.rent && typeof n.rent === 'object' && 'value' in n.rent
                ? n.rent
                : { value: n?.rent ?? 0 },
            trade:
              n?.trade && typeof n.trade === 'object' && 'value' in n.trade
                ? n.trade
                : { value: n?.trade ?? 0 },
            amount:
              n?.amount && typeof n.amount === 'object' && 'value' in n.amount
                ? n.amount
                : { value: n?.amount ?? 0 },
            category:
              n?.category && typeof n.category === 'object' && 'value' in n.category
                ? n.category
                : { value: n?.category ?? '' },
            interest:
              n?.interest && typeof n.interest === 'object' && 'value' in n.interest
                ? n.interest
                : { value: n?.interest ?? 0 },
            employment:
              n?.employment && typeof n.employment === 'object' && 'value' in n.employment
                ? n.employment
                : { value: n?.employment ?? 0 },
            taxclearance:
              n?.taxclearance && typeof n.taxclearance === 'object' && 'value' in n.taxclearance
                ? n.taxclearance
                : { value: n?.taxclearance ?? '' },
            yearofassessment:
              n?.yearofassessment &&
              typeof n.yearofassessment === 'object' &&
              'value' in n.yearofassessment
                ? n.yearofassessment
                : { value: n?.yearofassessment ?? '' },
          }))
        })(),
      },
    }

    // Keep original flat data for compatibility
    return {
      // Return the original flat data with entityData for visualization
      ...entityData,
      entity: nestedData.entity, // Also include entity at top level for initialValues
      person: nestedData.person, // Also include entity at top level for initialValues
      entityData: nestedData,
    }
  }
}

export const startFreshApplication = () => {}

export const cleanFileNameDynamic = (filename: string): string => {
  if (!filename) return ''

  // Get the file extension
  const extension = filename.includes('.') ? filename.split('.').pop()?.toLowerCase() : ''

  // Get the base name without extension
  const baseName = filename.includes('.')
    ? filename.substring(0, filename.lastIndexOf('.'))
    : filename

  // Split by hyphens and process parts
  const parts = baseName.split('-')

  // Handle year prefixes specially
  if (parts.length > 1 && ['y1', 'y2'].includes(parts[0])) {
    // Remove the year prefix but keep the rest of the structure
    parts.shift() // Remove the year prefix
  }

  // Remove the last part if it starts with 'cm', otherwise keep it
  const lastPart = parts[parts.length - 1]
  if (lastPart && lastPart.startsWith('c')) {
    parts.pop() // Remove the last part
  }

  // Join parts, remove all special characters, convert to lowercase
  let cleanName = parts
    .join('') // Join without hyphens
    .replace(/[-_\s()[\]{},.:;!@#$%^&*+=|/'"<>?]+/g, '') // Remove ALL special characters
    .toLowerCase()
    .trim()

  // Add extension back if it exists
  return extension ? `${cleanName}.${extension}` : cleanName
}
